import * as XLSX from "xlsx";
import type { BookingState } from "../contexts/BookingWizardContext";

export type ImportedBookingProduct = NonNullable<
  BookingState["products"]
>[number];

export const BOOKING_PRODUCT_IMPORT_COLUMNS = [
  { key: "name", label: "Product Name", required: true },
  { key: "hs_code", label: "HS Code", required: true },
  { key: "gross_weight", label: "Gross Weight (kg)", required: true },
  { key: "cbm", label: "CBM (m³)", required: true },
  { key: "value", label: "Value (USD)", required: true },
  { key: "ean", label: "EAN/Barcode", required: true },
  { key: "pieces", label: "Pieces", required: true },
  { key: "cartons", label: "Cartons", required: true },
] as const;

type ImportColumnKey = (typeof BOOKING_PRODUCT_IMPORT_COLUMNS)[number]["key"];

const HEADER_ALIASES: Record<ImportColumnKey, string[]> = {
  name: [
    "name",
    "product name",
    "productname",
    "product",
    "description",
    "naam",
    "title",
  ],
  hs_code: [
    "hs_code",
    "hs code",
    "hscode",
    "h.s. code",
    "h.s code",
    "harmonized code",
    "tariff code",
  ],
  gross_weight: [
    "gross_weight",
    "gross weight",
    "gross weight (kg)",
    "weight",
    "gewicht",
    "bruto gewicht",
  ],
  cbm: ["cbm", "cbm (m³)", "cbm (m3)", "volume", "m3", "cubic meters"],
  value: [
    "value",
    "value (usd)",
    "price",
    "amount",
    "waarde",
    "unit price",
    "total value",
  ],
  ean: [
    "ean",
    "ean/barcode",
    "barcode",
    "gtin",
    "upc",
    "product code",
    "item code",
  ],
  pieces: ["pieces", "pcs", "quantity", "qty", "stuks", "aantal"],
  cartons: ["cartons", "ctns", "carton", "boxes", "dozen"],
};

const normalizeHeader = (header: string): string =>
  header.toLowerCase().trim().replace(/\s+/g, " ");

const mapHeaderToKey = (header: string): ImportColumnKey | null => {
  const normalized = normalizeHeader(header);
  for (const col of BOOKING_PRODUCT_IMPORT_COLUMNS) {
    const aliases = HEADER_ALIASES[col.key];
    if (
      aliases.some(
        (alias) => normalized === alias || normalized.includes(alias)
      )
    ) {
      return col.key;
    }
  }
  return null;
};

const parseNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const cleaned = String(value)
    .replace(/,/g, ".")
    .replace(/[^\d.-]/g, "")
    .trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/** Digits-only EAN text (handles Excel scientific notation). */
export const parseEanText = (value: unknown): string => {
  if (value == null || value === "") return "";
  let raw = String(value).trim();
  if (/e[+-]/i.test(raw)) {
    const asNum = Number(raw);
    if (Number.isFinite(asNum)) {
      raw = asNum.toFixed(0);
    }
  }
  return raw.replace(/\D/g, "");
};

export type ImportPreviewRow = ImportedBookingProduct & {
  key: number;
  /** Source of truth for EAN length/editing (avoids number precision issues). */
  eanText: string;
};

export const downloadBookingProductImportTemplate = (): void => {
  const headers = BOOKING_PRODUCT_IMPORT_COLUMNS.map((col) => col.label);
  const sample = [
    "Sample Product 1",
    "8471.70.40",
    "10.5",
    "0.025",
    "100.00",
    "1234567890123",
    "10",
    "2",
  ];
  const worksheet = XLSX.utils.aoa_to_sheet([headers, sample]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
  XLSX.writeFile(workbook, "booking_products_template.xlsx");
};

export type BookingProductImportField =
  | "name"
  | "hs_code"
  | "ean"
  | "pieces"
  | "cartons"
  | "gross_weight"
  | "cbm"
  | "value";

/** Field-level validation keys (i18n under bookingWizard.productBulkImport.validation.*) */
export type BookingProductImportValidationKey =
  | "nameRequired"
  | "hsCodeRequired"
  | "eanRequired"
  | "eanLength"
  | "eanDigits"
  | "piecesRequired"
  | "cartonsRequired"
  | "grossWeightRequired"
  | "cbmRequired"
  | "valueRequired";

export type BookingProductFieldErrors = Partial<
  Record<BookingProductImportField, BookingProductImportValidationKey>
>;

/**
 * Same checks as editing a product in the booking product list:
 * name, HS code, EAN (required, exactly 13 digits), pieces, cartons, weight, CBM, value.
 */
export const validateImportedBookingProduct = (
  product: Pick<
    ImportedBookingProduct,
    | "name"
    | "hs_code"
    | "ean"
    | "pieces"
    | "cartons"
    | "gross_weight"
    | "cbm"
    | "value"
  > & { eanText?: string }
): BookingProductFieldErrors => {
  const errors: BookingProductFieldErrors = {};

  if (!product.name?.trim()) {
    errors.name = "nameRequired";
  }

  if (!product.hs_code?.toString().trim()) {
    errors.hs_code = "hsCodeRequired";
  }

  const eanRaw =
    product.eanText != null
      ? product.eanText.trim()
      : product.ean == null
        ? ""
        : String(product.ean).trim();
  if (!eanRaw) {
    errors.ean = "eanRequired";
  } else if (!/^\d+$/.test(eanRaw)) {
    errors.ean = "eanDigits";
  } else if (eanRaw.length !== 13) {
    errors.ean = "eanLength";
  }

  if (product.pieces == null || product.pieces < 1) {
    errors.pieces = "piecesRequired";
  }

  if (product.cartons == null || product.cartons < 1) {
    errors.cartons = "cartonsRequired";
  }

  if (product.gross_weight == null || product.gross_weight < 0.1) {
    errors.gross_weight = "grossWeightRequired";
  }

  if (product.cbm == null || product.cbm < 0.001) {
    errors.cbm = "cbmRequired";
  }

  if (product.value == null || product.value < 0) {
    errors.value = "valueRequired";
  }

  return errors;
};

export const hasImportedBookingProductErrors = (
  product: Parameters<typeof validateImportedBookingProduct>[0]
): boolean => Object.keys(validateImportedBookingProduct(product)).length > 0;

export const toImportedBookingProduct = (
  row: ImportPreviewRow
): ImportedBookingProduct => {
  const eanDigits = parseEanText(row.eanText);
  return {
    name: row.name?.trim() ?? "",
    hs_code: row.hs_code?.toString().trim() || null,
    ean: eanDigits ? Number(eanDigits) : null,
    pieces: row.pieces ?? null,
    cartons: row.cartons ?? null,
    gross_weight: row.gross_weight ?? null,
    cbm: row.cbm ?? null,
    value: row.value ?? null,
    isNew: true,
  };
};

export const productsToPreviewRows = (
  products: ImportedBookingProduct[]
): ImportPreviewRow[] =>
  products.map((product, index) => {
    const eanText =
      product.ean == null ? "" : parseEanText(product.ean);
    return {
      ...product,
      key: index,
      eanText,
    };
  });

export interface ParseBookingProductImportResult {
  products: ImportedBookingProduct[];
  skippedRows: number;
  errors: string[];
}

export const parseBookingProductImportFile = async (
  file: File
): Promise<ParseBookingProductImportResult> => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("empty_workbook");
  }

  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  }) as unknown[][];

  if (rows.length < 2) {
    throw new Error("insufficient_rows");
  }

  const headerRow = rows[0];
  if (!Array.isArray(headerRow)) {
    throw new Error("invalid_headers");
  }

  const headers = headerRow.map((cell) =>
    cell == null ? "" : String(cell).trim()
  );
  const columnIndexByKey = new Map<ImportColumnKey, number>();

  headers.forEach((header, index) => {
    if (!header) return;
    const key = mapHeaderToKey(header);
    if (key && !columnIndexByKey.has(key)) {
      columnIndexByKey.set(key, index);
    }
  });

  if (!columnIndexByKey.has("name")) {
    throw new Error("missing_name_column");
  }

  const products: ImportedBookingProduct[] = [];
  const errors: string[] = [];
  let skippedRows = 0;

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!Array.isArray(row)) {
      skippedRows += 1;
      continue;
    }

    const getCell = (key: ImportColumnKey): unknown => {
      const index = columnIndexByKey.get(key);
      return index == null ? undefined : row[index];
    };

    const nameRaw = getCell("name");
    const name = nameRaw == null ? "" : String(nameRaw).trim();
    if (!name) {
      const hasAnyData = row.some(
        (cell) => cell != null && String(cell).trim() !== ""
      );
      if (hasAnyData) {
        errors.push(`Row ${i + 1}: missing product name`);
      }
      skippedRows += 1;
      continue;
    }

    const rowText = row
      .map((cell) => (cell == null ? "" : String(cell).toLowerCase()))
      .join(" ");
    if (
      ["total", "subtotal", "grand total", "summary"].some((pattern) =>
        rowText.includes(pattern)
      )
    ) {
      skippedRows += 1;
      continue;
    }

    products.push({
      name,
      hs_code: (() => {
        const raw = getCell("hs_code");
        if (raw == null || String(raw).trim() === "") return null;
        return String(raw).trim();
      })(),
      gross_weight: parseNumber(getCell("gross_weight")),
      cbm: parseNumber(getCell("cbm")),
      value: parseNumber(getCell("value")),
      ean: (() => {
        const digits = parseEanText(getCell("ean"));
        return digits ? Number(digits) : null;
      })(),
      pieces: parseNumber(getCell("pieces")),
      cartons: parseNumber(getCell("cartons")),
      isNew: true,
    });
  }

  return { products, skippedRows, errors };
};
